import { deleteAsync, getAsync, postAsync, putAsync, runInBackground } from '../../shared/api.service'
import { queryString } from '../../shared/sso-workspace/auth-api'

// The client for rapid-api-notify (contract C-4, openapi.yaml), prefix
// /api/ddd. Like the content and auth direct-call modules: each call
// resolves to the response body and, on failure, throws the response
// ({ status, data: { message, code } }), so serverMessage(err) reads the
// server's sentence and <ErrorReference error={err} /> its request id.
//
// Background reads (R-BG): a poll the admin makes on its own -- the unread
// badge, the inbox refresh, capabilities, the health dashboard's Live --
// passes { background: true } and carries edxr-bg=1 (shared/background-flag),
// so the request log skips it and the person's last activity isn't bumped.
// A read the person started never carries it.
//
// Ids in paths are encoded; another organization's id answers 404
// notify.not_found, never 403 (C-4 Errors).
const base = () => process.env.REACT_APP_API_NOTIFY

// Whether this build knows where notify is (REACT_APP_API_NOTIFY): the
// header bell, the sidebar entries and Email activity's timeline link only
// appear where it does.
export const notifyAvailable = () => Boolean(base())

const unwrap = async call => {
  try {
    const result = await call()
    return result ? result.data : undefined
  } catch (err) {
    throw (err && err.response) || err
  }
}

const id = v => encodeURIComponent(String(v))

const get = (path, params, { background = false } = {}) => {
  const call = () => getAsync(base(), `api/ddd/${path}${queryString(params)}`)
  return unwrap(background ? () => runInBackground(call) : call)
}
const post = (path, body = {}) => unwrap(() => postAsync(base(), `api/ddd/${path}`, body))
const put = (path, body) => unwrap(() => putAsync(base(), `api/ddd/${path}`, body))
const del = path => unwrap(() => deleteAsync(base(), `api/ddd/${path}`))

// An action's optional reason ({ reason }), sent only when given.
const reasonBody = reason => (reason && String(reason).trim() ? { reason: String(reason).trim() } : {})

// A failed call that never reached the service (no network, the service
// down, CORS): the inbox's offline state, never an error reference.
export const isOffline = err => {
  if (!err) return false
  if (typeof err.status === 'number') return err.status === 0
  return !err.data
}

// ------------------------------------------------------------- self-scoped

// The inbox, newest first. filter: all (not archived) | unread | archived.
export const listInbox = ({ cursor, limit, filter } = {}, opts) => get('inbox', { cursor, limit, filter }, opts)
// { count, latestId, readThroughId }: refetch the list only when latestId moves.
export const unreadCount = opts => get('inbox/unread-count', undefined, opts)
export const markRead = notificationId => post(`inbox/${id(notificationId)}/read`)
export const markAllRead = () => post('inbox/read-all')
export const archive = notificationId => post(`inbox/${id(notificationId)}/archive`)

export const getPreferences = () => get('preference')
export const putPreferences = body => put('preference', body)
export const getOrganizationPreferences = () => get('preference/organization')
export const putOrganizationPreferences = body => put('preference/organization', body)

export const listDevices = () => get('device')
// Web Push: { platform: 'web', provider: 'webpush', token: <endpoint>, p256dh, auth, ... }.
// token, p256dh and auth are write-only: no response ever carries them.
export const registerDevice = body => post('device', body)
export const deviceHeartbeat = deviceId => post(`device/${id(deviceId)}/heartbeat`)
export const deleteDevice = deviceId => del(`device/${id(deviceId)}`)

export const getCapabilities = opts => get('notify/capabilities', undefined, opts)

// --------------------------------------------------------------------- ops
// scope: 'system' (SYS_NOTIFY read) or 'organization' (Email activity's
// rule: the caller's organization, USER update). Filters per C-4.

export const listOutbox = params => get('notify/outbox', params)
export const relayOutbox = outboxId => post(`notify/outbox/${id(outboxId)}/relay`)
export const listEvents = params => get('notify/events', params)
export const replayEvent = (eventId, reason) => post(`notify/events/${id(eventId)}/replay`, reasonBody(reason))
export const parkEvent = (eventId, reason) => post(`notify/events/${id(eventId)}/park`, reasonBody(reason))
export const listNotifications = params => get('notify/notifications', params)
export const listDeliveries = params => get('notify/deliveries', params)
export const resendDelivery = (deliveryId, reason) => post(`notify/deliveries/${id(deliveryId)}/resend`, reasonBody(reason))
export const parkDelivery = (deliveryId, reason) => post(`notify/deliveries/${id(deliveryId)}/park`, reasonBody(reason))
// { dryRun (default true), ids, provider, type, organizationId, since, until, reason }
export const drainDeliveries = body => post('notify/deliveries/drain', body)
export const listAttempts = params => get('notify/attempts', params)
export const getTimeline = (traceId, scope) => get(`notify/timeline/${id(traceId)}`, { scope })
export const listCaptures = params => get('notify/captures', params)
export const getCapture = captureId => get(`notify/captures/${id(captureId)}`)
export const getHealth = opts => get('notify/health', undefined, opts)
export const getMetrics = (params, opts) => get('notify/metrics', params, opts)
export const testSend = body => post('notify/test-send', body)

// Rollout switches (global and per organization) and the type catalogue.
// getSwitches(organizationId) adds `effective` for that organization; a
// change answers the full list. Omit organizationId for the global value;
// clearing means inherit.
export const getSwitches = organizationId => get('notify/switches', organizationId ? { organizationId } : undefined)
export const putSwitch = body => put('notify/switches', body)
export const clearSwitch = body => post('notify/switches/clear', body)
export const getCatalogue = () => get('notify/ops/catalogue')

// Push credentials: metadata only comes back; `secret` is write-only.
export const listCredentials = () => get('notify/credentials')
export const getCredential = credentialId => get(`notify/credentials/${id(credentialId)}`)
export const createCredential = body => post('notify/credentials', body)
export const testCredential = credentialId => post(`notify/credentials/${id(credentialId)}/test`)
export const activateCredential = (credentialId, graceHours) =>
  post(`notify/credentials/${id(credentialId)}/activate`, graceHours === undefined || graceHours === null || graceHours === '' ? {} : { graceHours: Number(graceHours) })
export const rotateCredential = (credentialId, body) => post(`notify/credentials/${id(credentialId)}/rotate`, body)
export const disableCredential = (credentialId, reason) => post(`notify/credentials/${id(credentialId)}/disable`, reasonBody(reason))
// A pending Web Push credential from a key pair notify makes itself
// ({ label, subject }; 201): the private key never leaves the server.
// rapid-api-notify's addition to C-4.
export const generateVapidCredential = ({ label, subject }) => post('notify/credentials/generate-vapid', { label, subject })

// A rotate whose new version failed its test answers 409 notify.conflict
// with { test: CredentialTestResult, credentialId: <the new, pending one> }:
// that, or null for any other failure.
export const rotateFailure = err => {
  const body = err && err.status === 409 && err.data && typeof err.data === 'object' ? err.data : null
  return body && body.test && typeof body.test === 'object' ? { test: body.test, credentialId: body.credentialId || null } : null
}

// ------------------------------------------------------------------ paging

// The ops lists' largest page (C-4 Cursors: 200), also the grid's block size.
export const OPS_PAGE_SIZE = 200

// An AG Grid infinite datasource (TableAgInfinite onGetRows) over C-4's
// keyset cursors. The row model asks by offset; each block's cursor is the
// one the page before it returned, recorded as pages arrive. A page whose
// cursor is null is the last, so the count closes there; until then it
// stays open (-1). A response for filters the page has left (isCurrent
// false) never lands.
export const makeCursorRows = ({ load, isCurrent, pageSize = OPS_PAGE_SIZE }) => {
  const cursors = new Map()

  return async (start, end) => {
    const limit = Math.min(end - start, pageSize, OPS_PAGE_SIZE)
    const cursor = start === 0 ? undefined : cursors.get(start)
    if (start > 0 && cursor === undefined) return { data: [], count: start }

    const page = (await load({ limit, cursor })) || {}
    if (!isCurrent()) return undefined

    const items = page.items || []
    if (page.cursor) cursors.set(start + items.length, page.cursor)
    return { data: items, count: page.cursor ? -1 : start + items.length }
  }
}
